import { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { RefundsApi } from '../../api/clients/refunds/refunds.api';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const BarChart = ({ showCount, data, role, isCustomPaymentTypesEnabled }) => {
  const [allowedRefundTypes, setAllowedRefundTypes] = useState([]);
  const [chartData, setChartData] = useState({
    labels: [],
    typeData: {},
    typeRefundMap: {},
  });

  const colorPalette = ['#0766AD', '#FF6C22', '#28a745', '#9C27B0', '#FF9800'];

  const fetchAllowedRefundTypes = async () => {
    try {
      if (role !== 'admin') {
        const response = await RefundsApi.getAllowedRefundTypes();
        setAllowedRefundTypes(response.data.allowed_refund_types);
      } else {
        setAllowedRefundTypes(['regular', 'insurance', 'business']);
      }
    } catch (error) {
      console.error('Error fetching allowed refund types:', error);
      // Fallback to showing regular only
      setAllowedRefundTypes(['regular']);
    }
  };

  useEffect(() => {
    if (!isCustomPaymentTypesEnabled) {
      fetchAllowedRefundTypes();
    }
  }, [role, isCustomPaymentTypesEnabled]);

  useEffect(() => {
    if (!data || data.length === 0) {
      setChartData({
        labels: [],
        typeData: {},
        typeRefundMap: {},
      });
      return;
    }

    const labels = data.map((item) => item.method);
    const typeData = {};

    const allTypes = [];
    data.forEach((item) => {
      item.types?.forEach((typeObj) => {
        const typeKey = typeObj.type;
        if (!allTypes.find((t) => t.type === typeKey)) {
          allTypes.push({
            type: typeKey,
            refundType: typeObj.refundType,
          });
        }
      });
    });

    allTypes.forEach(({ type }) => {
      typeData[type] = {
        amounts: [],
        counts: [],
      };
    });

    data.forEach((item) => {
      const typeMap = {};
      item.types?.forEach((typeObj) => {
        typeMap[typeObj.type] = typeObj;
      });

      allTypes.forEach(({ type }) => {
        typeData[type].amounts.push(typeMap[type]?.amount || 0);
        typeData[type].counts.push(typeMap[type]?.count || 0);
      });
    });

    setChartData({
      labels,
      typeData,
      typeRefundMap: allTypes.reduce((acc, { type, refundType }) => {
        acc[type] = refundType;
        return acc;
      }, {}),
    });
  }, [data]);

  const createDatasets = () => {
    const datasets = [];
    const availableTypes = Object.keys(chartData.typeData);

    availableTypes.forEach((type, index) => {
      let shouldIncludeType = true;

      if (!isCustomPaymentTypesEnabled) {
        const refundType = chartData.typeRefundMap?.[type];
        shouldIncludeType = allowedRefundTypes.includes(refundType);
      }

      if (shouldIncludeType) {
        const typeInfo = chartData.typeData[type];
        datasets.push({
          label: type,
          data: showCount ? typeInfo.counts : typeInfo.amounts,
          backgroundColor: colorPalette[index % colorPalette.length],
          borderWidth: 0,
          borderRadius: 5,
        });
      }
    });

    return datasets;
  };

  return (
    <Bar
      options={{
        plugins: {
          tooltip: {
            callbacks: {
              label: function (tooltipItem) {
                const datasetLabel = tooltipItem.dataset.label || '';
                const value = tooltipItem.formattedValue ?? '0.00';
                return showCount
                  ? `${datasetLabel}: ${value}`
                  : `${datasetLabel}: $${value}`;
              },
            },
          },
          legend: {
            display: true,
            labels: {
              boxWidth: 5,
            },
            position: 'top',
            textDirection: 'rtl',
          },
          datalabels: {
            display: false, // Disable numbers at the top of bars
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            stacked: true,
            ticks: {
              precision: 0,
              callback: function (value) {
                return showCount ? value : '$' + value;
              },
            },
            grid: {
              color: 'rgba(0, 0, 0, 0)',
            },
          },
          x: {
            stacked: true,
            grid: {
              color: 'rgba(0, 0, 0, 0)',
            },
          },
        },
      }}
      data={{
        labels: chartData.labels,
        datasets: createDatasets(),
      }}
    />
  );
};

export default BarChart;
